import React, { useState, useMemo } from 'react';

// Helper functions
const groupPlayByPeriod = (plays) => {
  if (!Array.isArray(plays)) return {};

  const grouped = {};
  plays.forEach(play => {
    if (!play) return;
    const period = play.Period || play.Inning || 1;
    if (!grouped[period]) {
      grouped[period] = [];
    }
    grouped[period].push(play);
  });
  return grouped;
};

const isScoringPlay = (play) => {
  if (!play) return false;
  return play.Points > 0 ||
    (play.Tags && play.Tags.includes('SCORE')) ||
    (play.Description && (
      play.Description.includes('made') ||
      play.Description.includes('score') ||
      play.Description.includes('goal')
    ));
};

const getPlayIcon = (tags, description) => {
  if (tags && tags.includes('SCORE')) {
    return (
      <div className="w-4 h-4 themePrimary rounded-full flex items-center justify-center">
        <div className="w-2 h-2 bg-white rounded-full"></div>
      </div>
    );
  }
  if (description && description.includes('foul')) {
    return (
      <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
        <div className="w-2 h-2 bg-white rounded-full"></div>
      </div>
    );
  }
  return (
    <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
  );
};

const formatTime = (time) => {
  if (!time) return '--:--';
  return time;
};

const PlayByPlay = (props = {}) => {
  // Destructure with defaults to ensure consistent props
  const {
    playByPlay = [],
    playByPlayLoading = false,
    playByPlayError = null,
    sportType = 'WBB',
    teamId = null,
    isVisible = true // Add visibility prop
  } = props;

  // HOOK 1: Always call useState - never conditional
  const [currentPage, setCurrentPage] = useState(1);

  // Static values - not hooks
  const playsPerPage = 20;

  // HOOK 2: Always call first useMemo - never conditional
  const allPlays = useMemo(() => {
    // Always return an array, even if empty
    if (!Array.isArray(playByPlay) || playByPlay.length === 0) {
      return [];
    }

    try {
      const groupedPlays = groupPlayByPeriod(playByPlay);
      const periods = Object.keys(groupedPlays).sort((a, b) => parseInt(a) - parseInt(b));

      const flattened = [];
      periods.forEach(period => {
        const periodPlays = groupedPlays[period];
        if (Array.isArray(periodPlays)) {
          periodPlays.forEach(play => {
            if (play) {
              flattened.push({ ...play, period });
            }
          });
        }
      });

      return flattened;
    } catch (error) {
      console.error('Error processing plays:', error);
      return [];
    }
  }, [playByPlay]);

  // Regular calculations - not hooks
  const totalPages = Math.max(1, Math.ceil(allPlays.length / playsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * playsPerPage;
  const endIndex = startIndex + playsPerPage;
  const currentPlays = allPlays.slice(startIndex, endIndex);

  // HOOK 3: Always call second useMemo - never conditional
  const currentGroupedPlays = useMemo(() => {
    const grouped = {};
    if (Array.isArray(currentPlays)) {
      currentPlays.forEach(play => {
        if (play && play.period) {
          if (!grouped[play.period]) {
            grouped[play.period] = [];
          }
          grouped[play.period].push(play);
        }
      });
    }
    return grouped;
  }, [currentPlays]);

  // Regular calculations - not hooks  
  const periods = Object.keys(currentGroupedPlays).sort((a, b) => parseInt(a) - parseInt(b));

  // Pagination function - defined inside component but not a hook
  const renderPaginationControls = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        const start = Math.max(1, safeCurrentPage - 2);
        const end = Math.min(totalPages, start + maxVisiblePages - 1);

        if (start > 1) {
          pages.push(1);
          if (start > 2) pages.push('...');
        }

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        if (end < totalPages) {
          if (end < totalPages - 1) pages.push('...');
          pages.push(totalPages);
        }
      }

      return pages;
    };

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
            disabled={safeCurrentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
            disabled={safeCurrentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, allPlays.length)}</span> of{' '}
              <span className="font-medium">{allPlays.length}</span> plays
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
                disabled={safeCurrentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>

              {getPageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                  {page === '...' ? (
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                      ...
                    </span>
                  ) : (
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${safeCurrentPage === page
                        ? 'z-10 themeSecondary text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                        }`}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
                disabled={safeCurrentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  // SINGLE RETURN STATEMENT - never early returns
  // Hide component using CSS instead of conditional rendering
  return (
    <div className={`space-y-6 ${!isVisible ? 'hidden' : ''}`}>
      {/* Loading State */}
      {playByPlayLoading && (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Loading play-by-play data...</p>
        </div>
      )}

      {/* Error State */}
      {!playByPlayLoading && playByPlayError && (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-600 text-center">{playByPlayError}</p>
        </div>
      )}

      {/* Empty State */}
      {!playByPlayLoading && !playByPlayError && allPlays.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-gray-600">No play-by-play data available for this game.</p>
        </div>
      )}

      {/* Content State */}
      {!playByPlayLoading && !playByPlayError && allPlays.length > 0 && (
        <>
          {periods.map(period => (
            <div key={period} className="bg-white rounded-lg shadow-sm border">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="text-lg font-semibold text-gray-800">
                  {(sportType === 'WBB' || sportType === 'MBB') ? `Period ${period}` : `Inning ${period}`}
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {currentGroupedPlays[period] && currentGroupedPlays[period].map((play, index) => (
                  <div
                    key={play.PlaySequence || `${period}-${index}`}
                    className={`p-4 hover:bg-gray-50 transition-colors ${isScoringPlay(play) ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className="flex-shrink-0 mt-1">
                          {getPlayIcon(play.Tags, play.Description)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {formatTime(play.Time)}
                            </span>
                            {play.Team && (
                              <span className={`text-xs px-2 py-1 rounded-full ${String(play.TeamID) === String(teamId)
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                                }`}>
                                {play.TeamAbbrev || play.Team}
                              </span>
                            )}
                            {isScoringPlay(play) && (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                +{play.Points || 0} pts
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700">{play.Description || 'No description available'}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4 text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {play.ScoreVis || 0} - {play.ScoreHome || 0}
                        </div>
                        <div className="text-xs text-gray-500">
                          {play.Visitor || 'Away'} vs {play.Home || 'Home'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Pagination */}
          {renderPaginationControls()}
        </>
      )}
    </div>
  );
};

export default PlayByPlay;